/**
 * HostPaymentSettingsTab (Iter 9.40 Pass K) - info-only payment share.
 * NOT an integration. Hosts paste their Venmo / Zelle / CashApp / QR. Phygital
 * never touches the money. Surfaces on the confirmation page.
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, Plus, X, Upload, Info } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: "venmo", label: "Venmo" },
  { value: "zelle", label: "Zelle" },
  { value: "cashapp", label: "CashApp" },
  { value: "apple_cash", label: "Apple Cash" },
  { value: "paypal", label: "PayPal" },
  { value: "bank", label: "Bank transfer" },
  { value: "qr_image", label: "QR code (image only)" },
  { value: "custom", label: "Other" },
];

export default function HostPaymentSettingsTab({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState("");
  const [methods, setMethods] = useState([]);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    fetch(`${API}/host/events/${eventId}/payment-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setText(d.payment_text || "");
        setMethods(d.payment_methods || []);
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  const updateMethod = (i, field, value) => {
    setMethods((prev) => prev.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)));
  };
  const addMethod = () => {
    setMethods((p) => [...p, { type: "venmo", handle: "", link: "", image: "", instructions: "" }]);
  };
  const removeMethod = (i) => setMethods((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/host/events/${eventId}/payment-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_text: text, payment_methods: methods }),
      });
      if (r.ok) toast.success("Payment info saved · shows on your confirmation page");
      else toast.error("Could not save");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (loading) return <Card><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></CardContent></Card>;

  return (
    <Card data-testid="host-payment-settings-tab">
      <CardContent className="p-5 sm:p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Payments (info-only share)</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              Phygital does not touch money. Paste your Venmo, Zelle, CashApp, Apple Cash, or upload a QR image - clients see this on their confirmation page and can copy/tap.
            </p>
          </div>
          <Badge variant="outline" className="text-[10px]">{methods.length} methods</Badge>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>Stripe is only used for your <strong>platform subscription</strong> (becoming a Solo/Team/Luxe host). It is never used for collecting client payments.</p>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-gray-700">Note shown above your payment options</label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Tips always appreciated - never required."
            className="text-sm"
            rows={2}
            data-testid="payment-note-input"
          />
        </div>

        <div className="space-y-3">
          {methods.map((m, i) => (
            <div key={i} className="border rounded-lg p-3 space-y-2 bg-gray-50" data-testid={`payment-method-${i}`}>
              <div className="flex items-center gap-2">
                <select
                  value={m.type}
                  onChange={(e) => updateMethod(i, "type", e.target.value)}
                  className="text-xs border rounded px-2 py-1 flex-shrink-0"
                  data-testid={`payment-type-${i}`}
                >
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <Input
                  value={m.handle}
                  onChange={(e) => updateMethod(i, "handle", e.target.value)}
                  placeholder={m.type === "qr_image" ? "(QR-only - leave blank)" : "@handle, email, or phone"}
                  className="text-sm h-8 flex-1"
                  data-testid={`payment-handle-${i}`}
                />
                <button onClick={() => removeMethod(i)} className="text-gray-400 hover:text-red-500 p-1" data-testid={`payment-remove-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <Input
                value={m.link}
                onChange={(e) => updateMethod(i, "link", e.target.value)}
                placeholder="Optional · https://venmo.com/your-handle"
                className="text-xs h-8"
                data-testid={`payment-link-${i}`}
              />
              <Input
                value={m.image}
                onChange={(e) => updateMethod(i, "image", e.target.value)}
                placeholder="Optional · QR or instruction image URL (Media Library upload soon)"
                className="text-xs h-8"
                data-testid={`payment-image-${i}`}
              />
              <Input
                value={m.instructions}
                onChange={(e) => updateMethod(i, "instructions", e.target.value)}
                placeholder="Optional · Short instructions (e.g. 'use my email')"
                className="text-xs h-8"
                data-testid={`payment-instructions-${i}`}
              />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addMethod} data-testid="payment-add-method-btn">
            <Plus className="w-4 h-4 mr-1" /> Add a payment method
          </Button>
        </div>

        <Button onClick={save} disabled={saving} className="w-full sm:w-auto h-10 bg-black hover:bg-gray-800" data-testid="payment-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Save payment info
        </Button>
      </CardContent>
    </Card>
  );
}
